import Papa from 'papaparse';
// Imported from the leaf `hubspotShared` module, NOT `./HubSpotMappingForm`
// directly — `HubSpotMappingForm.tsx` itself imports `suggestionsToMap` from
// THIS file (to build its `handleApplyTemplate`), so reaching back into it
// here would recreate the circular import `hubspotShared.tsx` exists to
// avoid (see that file's header comment). `HubSpotMappingForm.tsx`
// re-exports `RESERVED_PROPERTY_TARGETS` unchanged, so this is still "the
// same set the form module exports," just sourced one hop upstream of it.
import { RESERVED_PROPERTY_TARGETS } from './hubspotShared';
import type { HubSpotContactProperty, HubSpotDerivedSource, HubSpotPropertyMapWire } from './types';

// Task 9 (CSV template import) — pure parse/suggest logic, browser-free so it
// stays unit-testable without jsdom. `TemplateImport.tsx` owns the only
// browser-facing piece (reading the uploaded File via FileReader) and hands
// the resulting text straight to `suggestFromTemplate` below.
//
// Named `templateImportSuggestions.ts`, NOT `templateImport.ts` (the brief's
// literal filename) — deliberately, and disclosed here: it and
// `TemplateImport.tsx` would then differ ONLY by the first letter's case.
// On a case-insensitive filesystem (macOS/Windows — this one included),
// extension-less resolution of `./TemplateImport` probes `./TemplateImport.ts`
// BEFORE `.tsx`, and that probe's existence check matches THIS file
// case-insensitively — `HubSpotMappingForm.tsx`'s `import { TemplateImport }
// from './TemplateImport'` silently loaded THIS module instead of the
// component, so `TemplateImport` (undefined here) rendered as `<undefined
// .../>` and broke every test in `HubSpotMappingForm.test.tsx`, not just
// Task 9's — verified directly (`fs.existsSync('TemplateImport.ts')` and
// `fs.existsSync('templateImport.tsx')` both spuriously report `true` on
// this filesystem, though neither file exists under that exact name). Only
// this file was renamed — `TemplateImport.tsx` keeps the component name the
// brief and normal React convention both call for (filename matching the
// default/named export), and the test file stays
// `__tests__/templateImport.test.ts` per the brief (its OWN name has no
// collision — "templateImport.test.ts" and "TemplateImport.tsx" differ by
// more than case).

/**
 * One column's reviewed suggestion. `kind` decides the shape:
 *  - `native`: one of the six fields the adapter already writes natively —
 *    never produces `propertyMap` config (there's nothing to map).
 *  - `summary` / `derived`: the column's normalized name hit a fixed alias
 *    table (below) — `property` is a best-effort pre-match against the
 *    portal's HubSpot properties, or `null` when nothing matched (the
 *    operator picks one in the review table before Apply).
 *  - `static`: everything else THAT matched a HubSpot property by name —
 *    `value` is the template's own first non-empty example for the column.
 *  - `unmatched`: everything else that matched NO property — carried along
 *    with its example so the operator can see what the column held, but
 *    `suggestionsToMap` drops it (there's no target to write to yet).
 */
export type TemplateSuggestion =
  | { column: string; required: boolean; kind: 'native' }
  | { column: string; required: boolean; kind: 'summary'; property: string | null }
  | {
      column: string;
      required: boolean;
      kind: 'derived';
      property: string | null;
      source: HubSpotDerivedSource;
    }
  | { column: string; required: boolean; kind: 'static'; property: string | null; value: string }
  | { column: string; required: boolean; kind: 'unmatched'; example: string };

// Alias tables (normalized keys — see `normalize` below). Exact copies of the
// brief's spec: the six fields the adapter syncs natively regardless of any
// propertyMap, the column-name spellings that mean "route to the AI summary
// target", and the ones that mean "route to a fixed derived source".
const NATIVE = new Set([
  'first name',
  'last name',
  'email',
  'company',
  'company name',
  'phone',
  'phone number',
  'title',
]);

const SUMMARY_ALIASES = new Set(['additional call outs', 'additional callouts', 'conference notes']);

const DERIVED_ALIASES: Record<string, HubSpotDerivedSource> = {
  'event name': 'conference_name',
  'simpro event name': 'conference_name',
  'field rating': 'temperature',
};

/**
 * lowercase → non-alphanumerics to spaces → collapse whitespace → trim. Runs
 * on both the template's column headers and every candidate property's
 * `label`, so "SimPRO | Lead Source", "simpro-lead-source" and "  Simpro
 * Lead   Source " all converge on the same key.
 */
function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Best-effort property match for a summary/derived/static suggestion:
 * normalized `label` equality first, then containment either direction
 * (first hit wins, in the properties list's own order), else `null`.
 * Reserved targets (mirrors `RESERVED_PROPERTY_TARGETS`) are excluded from
 * candidacy entirely — a template import must never suggest a name the form
 * itself would flag as reserved.
 */
function matchProperty(
  normalizedColumn: string,
  properties: HubSpotContactProperty[] | null,
): string | null {
  if (!properties || !normalizedColumn) return null;
  const candidates = properties.filter((p) => !RESERVED_PROPERTY_TARGETS.has(p.name));

  const exact = candidates.find((p) => normalize(p.label) === normalizedColumn);
  if (exact) return exact.name;

  const contains = candidates.find((p) => {
    const label = normalize(p.label);
    return label.includes(normalizedColumn) || normalizedColumn.includes(label);
  });
  return contains ? contains.name : null;
}

/** First non-empty cell for `colIndex` across the example rows — "first",
 * not "last" or "most complete", per the template's own convention that row
 * order is significance order. */
function firstNonEmptyExample(exampleRows: string[][], colIndex: number): string {
  for (const row of exampleRows) {
    const cell = row[colIndex];
    if (cell) return cell;
  }
  return '';
}

/**
 * Parses a customer-supplied CSV export template and produces one
 * {@link TemplateSuggestion} per column, ready for operator review.
 *
 * Template shape (fixed, stated in `TemplateImport`'s caption): row 1 is
 * free-text guidance (ignored entirely), row 2 marks each column
 * Required/Optional (`required` = the cell starts with "Required",
 * case-insensitively — covers "Required", "required", and e.g. "Required 500
 * Characters"), row 3 holds the column names, and every row after that is
 * example data (the first NON-EMPTY cell per column wins — a blank in the
 * first example row falls through to the next row that has a value).
 *
 * Real CSV parsing (`Papa.parse`, not a hand-rolled comma splitter) so a
 * quoted example like `"Lead, qualified"` survives as one value instead of
 * splitting on its embedded comma.
 */
export function suggestFromTemplate(
  csvText: string,
  properties: HubSpotContactProperty[] | null,
): TemplateSuggestion[] {
  const { data } = Papa.parse<string[]>(csvText, { skipEmptyLines: 'greedy' });

  const requiredRow = data[1] ?? [];
  const nameRow = data[2] ?? [];
  const exampleRows = data.slice(3);

  const suggestions: TemplateSuggestion[] = [];

  nameRow.forEach((rawColumn, colIndex) => {
    const column = (rawColumn ?? '').trim();
    if (!column) return; // a ragged trailing empty header cell — nothing to suggest

    const required = /^required/i.test((requiredRow[colIndex] ?? '').trim());
    const example = firstNonEmptyExample(exampleRows, colIndex);
    const normalized = normalize(column);

    if (NATIVE.has(normalized)) {
      suggestions.push({ column, required, kind: 'native' });
      return;
    }

    if (SUMMARY_ALIASES.has(normalized)) {
      suggestions.push({
        column,
        required,
        kind: 'summary',
        property: matchProperty(normalized, properties),
      });
      return;
    }

    const derivedSource = DERIVED_ALIASES[normalized];
    if (derivedSource) {
      suggestions.push({
        column,
        required,
        kind: 'derived',
        property: matchProperty(normalized, properties),
        source: derivedSource,
      });
      return;
    }

    const property = matchProperty(normalized, properties);
    if (property) {
      suggestions.push({ column, required, kind: 'static', property, value: example });
      return;
    }

    suggestions.push({ column, required, kind: 'unmatched', example });
  });

  return suggestions;
}

/**
 * Reduces a reviewed suggestion set to the same wire shape the form itself
 * submits (`HubSpotPropertyMapWire`). Skips `native` (nothing to map) and
 * `unmatched` (no target chosen) suggestions outright, and skips ANY
 * summary/derived/static suggestion still carrying `property: null` — the
 * operator hasn't picked a property for it in the review table yet, so it
 * isn't ready to apply. Empty sections are omitted, matching the wire type's
 * own convention (and `HubSpotMappingForm`'s `toWire`).
 */
export function suggestionsToMap(suggestions: TemplateSuggestion[]): HubSpotPropertyMapWire {
  const map: HubSpotPropertyMapWire = {};
  const staticEntries: Record<string, string> = {};
  const derivedEntries: Record<string, HubSpotDerivedSource> = {};

  for (const suggestion of suggestions) {
    if (suggestion.kind === 'native' || suggestion.kind === 'unmatched') continue;
    if (suggestion.property === null) continue;

    if (suggestion.kind === 'summary') {
      map.description = suggestion.property;
    } else if (suggestion.kind === 'static') {
      staticEntries[suggestion.property] = suggestion.value;
    } else {
      derivedEntries[suggestion.property] = suggestion.source;
    }
  }

  if (Object.keys(staticEntries).length > 0) map.static = staticEntries;
  if (Object.keys(derivedEntries).length > 0) map.derived = derivedEntries;

  return map;
}

/**
 * Reshapes a suggestion to a NEW `kind` when the operator overrides the
 * auto-classification in `TemplateImport`'s review table's Kind select,
 * carrying over whatever still makes sense rather than dropping the row back
 * to blank. Pure and deterministic — moved here (not left in `TemplateImport.tsx`,
 * where it originated) specifically so its branches are unit-tested directly,
 * same as every other decision this module makes; `TemplateImport.tsx` only
 * calls it from the Kind cell's `onChange`.
 *
 * Per target `kind`:
 *  - `native`: drops everything except `column`/`required` — a native field
 *    is never property-, value-, or source-mapped.
 *  - `summary` / `static` / `derived`: carry the source's own `property`
 *    forward when the source HAD one (`'property' in suggestion` — true for
 *    summary/static/derived sources, even when that property is `null`);
 *    `native`/`unmatched` sources have no `property` field at all, so the
 *    result gets `null`.
 *  - `derived` additionally carries the source's own `source` field forward
 *    when present (i.e. the source was ALREADY `derived`), else defaults to
 *    `'conference_name'` — there's no "prior source" to preserve otherwise.
 *  - `static` / `unmatched` both carry forward the same underlying text,
 *    just under a different field name (`static`'s `value` vs `unmatched`'s
 *    `example`): the source's own `value` if it had one (i.e. was `static`),
 *    else its own `example` if it had one (i.e. was `unmatched`), else `''`.
 *  - Retargeting to the SAME kind a suggestion already has is a no-op that
 *    reconstructs an equal (not identical-by-reference) object — every field
 *    round-trips through the rules above unchanged.
 */
export function reshapeForKind(
  suggestion: TemplateSuggestion,
  kind: TemplateSuggestion['kind'],
): TemplateSuggestion {
  const { column, required } = suggestion;
  const priorProperty = 'property' in suggestion ? suggestion.property : null;
  const priorText = 'value' in suggestion ? suggestion.value : 'example' in suggestion ? suggestion.example : '';

  switch (kind) {
    case 'native':
      return { column, required, kind: 'native' };
    case 'summary':
      return { column, required, kind: 'summary', property: priorProperty };
    case 'derived':
      return {
        column,
        required,
        kind: 'derived',
        property: priorProperty,
        source: 'source' in suggestion ? suggestion.source : 'conference_name',
      };
    case 'static':
      return { column, required, kind: 'static', property: priorProperty, value: priorText };
    case 'unmatched':
    default:
      return { column, required, kind: 'unmatched', example: priorText };
  }
}
